/*
 * Copyright (c) 2025. Orion Analytics. All Rights Reserved
 */

import React, { useState } from "react";
import Plot from "react-plotly.js";

const CouponReinvestmentVisualizer = () => {
  const [faceValue, setFaceValue] = useState(1000);
  const [couponRate, setCouponRate] = useState(7);
  const [years, setYears] = useState(10);
  const [reinvestmentRate, setReinvestmentRate] = useState(6);

  const couponAmount = (faceValue * couponRate) / 100;

  const valuesWithReinvestment = [];
  const valuesWithoutReinvestment = [];
  const reinvestedYTM = [];
  const simpleYTM = [];

  let reinvested = 0;
  let simple = 0;

  for (let year = 1; year <= years; year++) {
    reinvested = (reinvested + couponAmount) * (1 + reinvestmentRate / 100);
    simple += couponAmount;

    const totalWithReinvestment = reinvested + faceValue;
    const totalWithoutReinvestment = simple + faceValue;

    const ytmWith = Math.pow(totalWithReinvestment / faceValue, 1 / year) - 1;
    const ytmWithout = Math.pow(totalWithoutReinvestment / faceValue, 1 / year) - 1;

    valuesWithReinvestment.push({ year, value: totalWithReinvestment });
    valuesWithoutReinvestment.push({ year, value: totalWithoutReinvestment });
    reinvestedYTM.push({ year, ytm: ytmWith * 100 }); // %
    simpleYTM.push({ year, ytm: ytmWithout * 100 }); // %
  }

  const totalWithReinvestment = valuesWithReinvestment.at(-1).value;
  const totalWithoutReinvestment = valuesWithoutReinvestment.at(-1).value;

  const data = [
    {
      x: valuesWithReinvestment.map((v) => v.year),
      y: valuesWithReinvestment.map((v) => v.value),
      name: "With Reinvestment",
      mode: "lines+markers",
      line: { color: "green", width: 3 },
      yaxis: "y1"
    },
    {
      x: valuesWithoutReinvestment.map((v) => v.year),
      y: valuesWithoutReinvestment.map((v) => v.value),
      name: "Without Reinvestment",
      mode: "lines+markers",
      line: { color: "red", width: 3, dash: "dash" },
      yaxis: "y1"
    },
    {
      x: reinvestedYTM.map((v) => v.year),
      y: reinvestedYTM.map((v) => v.ytm),
      name: "Reinvested YTM (%)",
      mode: "lines+markers",
      line: { color: "blue", width: 2, dash: "dot" },
      yaxis: "y2"
    },
    {
      x: simpleYTM.map((v) => v.year),
      y: simpleYTM.map((v) => v.ytm),
      name: "Simple YTM (%)",
      mode: "lines+markers",
      line: { color: "orange", width: 2, dash: "dashdot" },
      yaxis: "y2"
    }
  ];

  const annotations = [
    {
      x: years,
      y: totalWithReinvestment,
      text: `With Reinvestment: ₹${totalWithReinvestment.toFixed(2)}`,
      showarrow: true,
      arrowhead: 6,
      ax: -50,
      ay: -40,
      font: { color: "green" }
    },
    {
      x: years,
      y: totalWithoutReinvestment,
      text: `Without Reinvestment: ₹${totalWithoutReinvestment.toFixed(2)}`,
      showarrow: true,
      arrowhead: 6,
      ax: -50,
      ay: 40,
      font: { color: "red" }
    }
  ];

  return (
    <div className="col-md-6 col-lg-12 wow fadeInUp" style={{ marginBottom: "20px" }} data-wow-delay="0.6s">
      <div className="service-item">
        <h3>💸 Coupon Reinvestment Visualizer</h3>

        <label >
          Coupon Rate: {couponRate}%
          <input type="range" min={1} max={15} step={0.5} value={couponRate} onChange={(e) => setCouponRate(Number(e.target.value))} />
        </label>
        <label>
          Maturity: {years} years
          <input type="range" min={1} max={30} value={years} onChange={(e) => setYears(Number(e.target.value))} />
        </label>
        <label>
          Reinvestment Rate: {reinvestmentRate}%
          <input type="range" min={0} max={15} step={0.5} value={reinvestmentRate} onChange={(e) => setReinvestmentRate(Number(e.target.value))} />
        </label>
        <br />

        <Plot
          data={data}
          layout={{
            title: "Total Value and YTM (With & Without Reinvestment)",
            xaxis: { title: "Years" },
            yaxis: { title: "Cumulative Value (₹)", side: "left" },
            yaxis2: {
              title: "Yield to Maturity (%)",
              overlaying: "y",
              side: "right",
              showgrid: true
            },
            annotations,
            legend: { x: 0, y: -0.2, orientation: "h" },
            margin: { t: 60, l: 60, r: 60, b: 60 }
          }}
          style={{ width: "100%", height: "500px", maxWidth: "1000px" }}
        />
      </div>
    </div>
  );
};

export default CouponReinvestmentVisualizer;

